<template>
  <PlanBanner
    v-if="planStore.id"
    @save="handleSave"
  />

  <AppContentLoader
    v-if="planStore.loading"
    text="Loading plan..."
  />

  <h1
    v-else-if="!planStore.id || !frameworks"
    class="flex items-center justify-center h-full"
  >
    Unable to load plan. Please try again.
  </h1>

  <main
    v-else
    class="plan-page"
  >
    <RouterView :frameworks="frameworks" />
  </main>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { DataSet } from 'vis-data';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';

import { AppContentLoader } from '@/components/app/index.js';
import { PlanBanner } from '@/components/banners/index.js';

import { useDateFormatter, useHttp, useToasts } from '@/composables/index.js';
import { usePlanStore } from '@/store/index.js';

const route = useRoute();
const planStore = usePlanStore();
const toasts = useToasts();
const dateFormatter = useDateFormatter();

const frameworks = ref(null);

const errors = ref(null);
const initialPlanStoreLoad = ref(false);

planStore.loading = true;

const loadPlan = async () => {
  initialPlanStoreLoad.value = true;
  const response = await useHttp().get(`/districts/${route.params.district}/plans/${route.params.plan}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    let { id, name, introduction, executiveSummary, conclusion, nodes, edges, vision, logo, provincialLogo, district } = response.data;

    nodes = new DataSet(nodes || []);
    edges = new DataSet(edges || []);

    const lastPublishedAt = dateFormatter.formatToISO();

    planStore.$patch({
      id, name, introduction, executiveSummary, conclusion, vision, nodes, edges, lastPublishedAt, logo, provincialLogo, district,
    });
  }

  await nextTick();
  initialPlanStoreLoad.value = false;
};

const loadFrameworks = async () => {
  const response = await useHttp().get('/util/frameworks');

  if (response.statusCode === HttpStatusCode.Ok) {
    frameworks.value = response.data.items;
  }
};

// Handle browser refresh or tab close - triggers browser warning
const handleBeforeUnload = (e) => {
  if (planStore.isDirty) {
    e.preventDefault();
    e.returnValue = '';
  }
};

// Handle in-app router navigation
// This triggers a standard JS confirm, dialog when navigating to other views
onBeforeRouteLeave((to, from, next) => {
  if (planStore.isDirty && !import.meta.env.DEV) {
    const answer = window.confirm('You have unsaved changes. Are you sure you want to leave? Click Save to keep your work.');

    if (answer) {
      next(); // Proceed
    } else {
      next(false); // Cancel navigation
    }
  } else {
    next(); // No changes, proceed normaly
  }
});

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);

  // Listener for browser close/refresh event
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

const uploadLogo = async () => new Promise(async (resolve, reject) => {
  if (planStore.logo instanceof File) {
    const data = new FormData();

    data.set('logo', planStore.logo);

    const response = await useHttp().post(`/districts/${route.params.district}/plans/${route.params.plan}/logo`, data);

    if (response.statusCode === HttpStatusCode.Ok) {
      resolve(response);
    } else {
      reject(response);
    }
  }

  resolve(null);
});

const uploadProvincialLogo = async () => new Promise(async (resolve, reject) => {
  if (planStore.provincialLogo instanceof File) {
    const data = new FormData();

    data.set('provincialLogo', planStore.provincialLogo);

    const response = await useHttp().post(`/districts/${route.params.district}/plans/${route.params.plan}/provincial-logo`, data);

    if (response.statusCode === HttpStatusCode.Ok) {
      resolve(response);
    } else {
      reject(response);
    }
  }

  resolve(null);
});

const savePlan = async () => new Promise(async (resolve, reject) => {
  const response = await useHttp().put(`districts/${route.params.district}/plans/${route.params.plan}`, planStore.formData);

  errors.value = null;

  if (response.statusCode === HttpStatusCode.Ok) {
    resolve(response);
    planStore.isDirty = false;
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    // Save the errors into a variable to display them to the user
    errors.value = response.errors;
    reject(response);
  } else {
    toasts.error('Failed to save Plan');
    reject(response);
  }
});

const handleKeyDown = (e) => {
  // Check if "s" is pressed AND (Control key OR Command key for Mac)
  if ((e.ctrlKey || e.metaKey) && e.key === 's') {
    e.preventDefault(); // Stop the "Save Page As" dialog

    savePlan();
  }
};

const handleSave = async () => {
  planStore.saving = true;

  await Promise.all([
    uploadLogo(),
    uploadProvincialLogo(),
    savePlan(),
  ])
    .then(() => {
      toasts.success('Your changes have been saved');
    })
    .catch(async () => {
      if (errors.value) {
        // 1. Flatten the nested error object into a simple array of messages
        const errorMessages = flattenErrors(errors.value);
        // 2. Loop through the simple array and display each message as a toast
        if (errorMessages.length > 0) {
          for (const message of errorMessages) {
            toasts.error(message);
            await delay(1000);
          }
        } else {
          // Fallback if errors.value was set but somehow empty
          toasts.error('There were validation errors saving your changes');
        }
      } else {
        // Fallback for non-validation errors (e.g., 500, 404)
        toasts.error('There were errors saving your changes');
      }
    });

  planStore.saving = false;
};

Promise
  .all([
    loadPlan(),
    loadFrameworks(),
  ])
  .finally(() => {
    planStore.loading = false;
  });

// Add the toasts to the store with a delay so that each one can be seen
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * The errors are coming back in an object that isn't easy to use, so we need to format it into something we can use to display the errors.
 *
 * Recursively flattens a nested Symfony validation error object into an array of messages.
 * @param {object|array} errorObject The nested error object (e.g., response.errors)
 * @returns {string[]} An array of all error messages.
 */
const flattenErrors = (errorObject) => {
  let messages = [];

  // If it's an array of strings
  if (Array.isArray(errorObject)) {
    messages.push(...errorObject);
  // If it's a nested object (complex field errors)
  } else if (typeof errorObject === 'object' && errorObject !== null) {
    for (const key in errorObject) {
      if (Object.prototype.hasOwnProperty.call(errorObject, key)) {
        // Recursively call for nested properties
        messages.push(...flattenErrors(errorObject[key]));
      }
    }
  }

  return messages;
};

// Watch the store for any changes to set the dirty flag
watch(() => planStore.formData, () => {
  if (!initialPlanStoreLoad.value) {
    planStore.isDirty = true;
  }
}, { deep: true });
</script>
